import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import moment from 'moment';
import React, { useCallback, useMemo, useState } from 'react';
import {
  usePerformersQuery,
  usePerformerTypesQuery,
} from '@/queries/performers';
import {
  usePerformancesQuery,
  useRemovePerformanceMutation,
  useRemoveStageMutation,
  useSavePerformanceMutation,
  useSaveStageMutation,
  useStagesQuery,
} from '@/queries/stages';
import { toEpochMs } from '@/lib/schedule-datetime';
import {
  withServerDateFields,
  withServerDateFieldsList,
} from '@/lib/server-date';
import { LocationMapField } from '../../controls';
import Portal from '../../modal/Portal';
import { PerformanceForm } from '../../performances';
import { StageForm } from '../../stages';
import { EventRelatedSection } from '../EventRelatedSection';
import { StageSponsorsGrid } from './StageSponsorsGrid';

function formatStageHours(times) {
  if (!times?.length) {
    return null;
  }
  return times
    .map((slot) => {
      const startMs = toEpochMs(slot.startTimeMS, slot.startTime);
      const endMs = toEpochMs(slot.endTimeMS, slot.endTime);
      if (startMs == null && endMs == null) {
        return null;
      }
      if (startMs != null && endMs != null) {
        const sameDay = moment(startMs).isSame(endMs, 'day');
        if (sameDay) {
          return `${moment(startMs).format('MMM D, YYYY')} · ${moment(startMs).format('h:mm A')} – ${moment(endMs).format('h:mm A')}`;
        }
        return `${moment(startMs).format('MMM D, YYYY h:mm A')} – ${moment(endMs).format('MMM D, YYYY h:mm A')}`;
      }
      if (startMs != null) {
        return moment(startMs).format('MMM D, YYYY h:mm A');
      }
      return moment(endMs).format('MMM D, YYYY h:mm A');
    })
    .filter(Boolean);
}

function formatPerfDate(performance) {
  const ms = toEpochMs(performance.startTimeMS, performance.startTime);
  return ms != null ? moment(ms).format('MMM D, YYYY') : '—';
}

function formatPerfTime(performance, which) {
  const ms =
    which === 'start'
      ? toEpochMs(performance.startTimeMS, performance.startTime)
      : toEpochMs(performance.endTimeMS, performance.endTime);
  return ms != null ? moment(ms).format('h:mm A') : '—';
}

export function StagesSection({ festivalId }) {
  const stagesQ = useStagesQuery(festivalId);
  const perfQ = usePerformancesQuery(festivalId);
  const performersQ = usePerformersQuery();
  const performerTypesQ = usePerformerTypesQuery();

  const saveStageMutation = useSaveStageMutation(festivalId);
  const removeStageMutation = useRemoveStageMutation(festivalId);
  const savePerformanceMutation = useSavePerformanceMutation(festivalId);
  const removePerformanceMutation = useRemovePerformanceMutation(festivalId);

  const stages = stagesQ.data ?? [];
  const performances = perfQ.data ?? [];
  const performers = performersQ.data ?? [];
  const performerTypes = performerTypesQ.data ?? {};

  const loading =
    festivalId == null ||
    stagesQ.isPending ||
    stagesQ.isFetching ||
    perfQ.isPending ||
    perfQ.isFetching ||
    performersQ.isPending ||
    performersQ.isFetching;

  const [infoModalOpen, setInfoModalOpen] = useState(false);
  const [selectedStage, setSelectedStage] = useState({});
  const [perfModalOpen, setPerfModalOpen] = useState(false);
  const [selectedPerformance, setSelectedPerformance] = useState({});
  const [perfStage, setPerfStage] = useState(null);
  const [stagePendingDelete, setStagePendingDelete] = useState(null);

  const onFileUpload = useCallback((fileInfo) => {
    const { url, filename } = fileInfo;
    setSelectedStage((prev) => ({
      ...prev,
      photoURL: url,
      stagePhoto: filename,
      photo: filename,
    }));
  }, []);

  const onFileDelete = useCallback(() => {
    setSelectedStage((prev) => ({
      ...prev,
      photoURL: null,
      stagePhoto: null,
      photo: null,
    }));
  }, []);

  const openInfoModal = useCallback(
    (stage) => {
      const selected =
        stage?.stageId != null &&
        stages.some((s) => s.stageId === stage.stageId)
          ? stage
          : {};
      setSelectedStage(selected);
      setInfoModalOpen(true);
    },
    [stages],
  );

  const handleDateChange = useCallback((dates) => {
    setSelectedStage((prev) => ({ ...prev, times: dates }));
  }, []);

  const handleLocationChange = useCallback((location, displayName) => {
    setSelectedStage((prev) => {
      const updatedLocation = {
        ...prev.location,
        ...location,
        ...(displayName != null ? { locationName: displayName } : null),
        radius: 50,
      };
      return {
        ...prev,
        location: updatedLocation,
        ...(displayName != null ? { locationName: displayName } : null),
      };
    });
  }, []);

  const handleLocationNameChange = useCallback((locationName) => {
    setSelectedStage((prev) => ({
      ...prev,
      locationName,
      location: {
        ...prev.location,
        locationName,
      },
    }));
  }, []);

  const handleStageSave = useCallback(
    async (stage) => {
      const updatedStage = { ...selectedStage, ...stage };
      if (updatedStage.times) {
        updatedStage.times = withServerDateFieldsList(updatedStage.times);
      }
      delete updatedStage.performances;
      delete updatedStage.performers;

      try {
        await saveStageMutation.mutateAsync(updatedStage);
        setInfoModalOpen(false);
        setSelectedStage({});
      } catch {
        /* mutation toast */
      }
    },
    [selectedStage, saveStageMutation],
  );

  const dictOfPerformances = useMemo(
    () =>
      performances.reduce((dict, performance) => {
        if (!dict[performance.stageId]) {
          dict[performance.stageId] = [performance];
        } else {
          dict[performance.stageId].push(performance);
        }
        return dict;
      }, {}),
    [performances],
  );

  const openPerformanceModal = useCallback((stage, performance = {}) => {
    const original =
      performance?.performanceId != null
        ? performance
        : { stageId: stage.stageId };
    setPerfStage(stage);
    setSelectedPerformance({ ...original, stageId: stage.stageId });
    setPerfModalOpen(true);
  }, []);

  const handlePerformanceDateChange = useCallback((dates) => {
    setSelectedPerformance((prev) => ({ ...prev, ...dates[0] }));
  }, []);

  const performerUpdated = useCallback((performer) => {
    setSelectedPerformance((prev) => ({
      ...prev,
      performer,
      performerId: performer.performerId,
    }));
  }, []);

  const savePerformance = useCallback(
    async (performance) => {
      const updatedPerformance = withServerDateFields({
        ...selectedPerformance,
        ...performance,
        stageId: perfStage?.stageId ?? selectedPerformance.stageId,
      });
      delete updatedPerformance.dates;
      delete updatedPerformance.performerName;
      delete updatedPerformance.stage;

      try {
        await savePerformanceMutation.mutateAsync(updatedPerformance);
        setPerfModalOpen(false);
        setSelectedPerformance({});
        setPerfStage(null);
      } catch {
        /* mutation toast */
      }
    },
    [perfStage, savePerformanceMutation, selectedPerformance],
  );

  const deletePerformanceRow = useCallback(
    async (performance) => {
      if (!performance?.performanceId) {
        return;
      }
      if (
        typeof window !== 'undefined' &&
        !window.confirm(
          `Remove ${performance.performer?.performerName || 'this performance'}?`,
        )
      ) {
        return;
      }
      try {
        await removePerformanceMutation.mutateAsync(performance.performanceId);
      } catch {
        /* mutation toast */
      }
    },
    [removePerformanceMutation],
  );

  const requestDeleteStage = useCallback((stage) => {
    if (!stage?.stageId) {
      return;
    }
    setStagePendingDelete(stage);
  }, []);

  const cancelDeleteStage = useCallback(() => {
    setStagePendingDelete(null);
  }, []);

  const confirmDeleteStage = useCallback(async () => {
    const stage = stagePendingDelete;
    if (!stage?.stageId) {
      return;
    }
    try {
      await removeStageMutation.mutateAsync(stage.stageId);
      setStagePendingDelete(null);
    } catch {
      /* mutation toast */
    }
  }, [removeStageMutation, stagePendingDelete]);

  return (
    <>
      <EventRelatedSection
        icon={['fas', 'microphone-lines']}
        title="Stages"
        loading={loading}
        contentOnly
        addPlacement="header"
        onAdd={() => openInfoModal({})}
        addLabel="Add Stage"
        addButtonClassName="btn-add-section--primary"
        emptyMessage=""
      >
        {!loading && stages.length === 0 ? (
          <p className="event-related-empty">None yet.</p>
        ) : null}

        <div className="stage-cards">
          {stages.map((stage) => {
            const stagePerformances = dictOfPerformances[stage.stageId] ?? [];
            const hours = formatStageHours(stage.times);
            const locationLabel =
              stage.location?.locationName || stage.locationName || null;

            return (
              <article key={stage.stageId} className="stage-card-block">
                <div className="stage-card-header">
                  <div className="stage-card-header-main">
                    <h3 className="m-0 mb-3 text-2xl font-bold leading-tight text-[#121212] sm:text-3xl">
                      {stage.stageName || 'Untitled stage'}
                    </h3>

                    {hours?.length ? (
                      <ul className="stage-hours-list">
                        {hours.map((line) => (
                          <li key={line}>
                            <FontAwesomeIcon icon={['fas', 'clock']} />
                            <span>{line}</span>
                          </li>
                        ))}
                      </ul>
                    ) : null}
                  </div>

                  {stage.photoURL ? (
                    <div className="stage-card-photo">
                      <img
                        src={stage.photoURL}
                        alt={stage.stageName || 'Stage'}
                      />
                    </div>
                  ) : null}

                  <div className="stage-card-header-actions">
                    <button
                      type="button"
                      className="bethere-action-btn"
                      aria-label={`Edit ${stage.stageName || 'stage'}`}
                      title="Edit stage"
                      onClick={() => openInfoModal(stage)}
                    >
                      <FontAwesomeIcon icon={['fas', 'pen']} />
                    </button>
                    <button
                      type="button"
                      className="bethere-action-btn bethere-action-btn--danger"
                      aria-label={`Delete ${stage.stageName || 'stage'}`}
                      title="Delete stage"
                      onClick={() => requestDeleteStage(stage)}
                    >
                      <FontAwesomeIcon icon={['fas', 'trash']} />
                    </button>
                  </div>
                </div>

                {locationLabel || stage.location ? (
                  <div className="stage-location mb-4">
                    {locationLabel ? (
                      <p className="mb-2 flex items-start gap-2 text-sm text-[#121212]">
                        <FontAwesomeIcon
                          icon={['fas', 'location-dot']}
                          className="mt-0.5 text-[var(--bethere-primary)]"
                        />
                        <span>{locationLabel}</span>
                      </p>
                    ) : null}
                    {stage.location ? (
                      <div className="map-box overflow-hidden rounded-[var(--bethere-radius-md)]">
                        <LocationMapField
                          location={stage.location}
                          locationName={locationLabel || ''}
                          readOnly
                          showSearch={false}
                        />
                      </div>
                    ) : null}
                  </div>
                ) : null}

                {stage.stageDescription ? (
                  <p className="mb-4 whitespace-pre-wrap text-sm leading-relaxed text-[var(--bethere-f-p)] text-[#121212]">
                    {stage.stageDescription}
                  </p>
                ) : null}

                <div className="stage-performances mb-6">
                  <h4 className="mb-3 text-sm font-bold uppercase tracking-[0.5px] text-[#121212]">
                    Performances
                  </h4>
                  <div className="table-responsive">
                    <table className="stage-performances-table">
                      <thead>
                        <tr>
                          <th scope="col">Performer</th>
                          <th scope="col">Date</th>
                          <th scope="col">Start Time</th>
                          <th scope="col">End Time</th>
                          <th scope="col">
                            <span className="sr-only">Actions</span>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {stagePerformances.length === 0 ? (
                          <tr>
                            <td
                              colSpan={5}
                              className="text-[var(--muted-foreground)]"
                            >
                              No performances yet.
                            </td>
                          </tr>
                        ) : (
                          stagePerformances.map((performance) => {
                            const notes = performance.performanceNotes?.trim();
                            return (
                              <tr key={performance.performanceId}>
                                <td>
                                  {performance.performer?.performerName || '—'}
                                </td>
                                <td>{formatPerfDate(performance)}</td>
                                <td>{formatPerfTime(performance, 'start')}</td>
                                <td>{formatPerfTime(performance, 'end')}</td>
                                <td>
                                  <div className="stage-perf-actions">
                                    <button
                                      type="button"
                                      className="stage-perf-action-btn"
                                      aria-label="Performance notes"
                                      title={notes || 'No performance notes'}
                                      disabled={!notes}
                                    >
                                      <FontAwesomeIcon
                                        icon={['fas', 'circle-info']}
                                      />
                                    </button>
                                    <button
                                      type="button"
                                      className="stage-perf-action-btn"
                                      aria-label="Edit performance"
                                      title="Edit performance"
                                      onClick={() =>
                                        openPerformanceModal(stage, performance)
                                      }
                                    >
                                      <FontAwesomeIcon icon={['fas', 'pen']} />
                                    </button>
                                    <button
                                      type="button"
                                      className="stage-perf-action-btn stage-perf-action-btn--danger"
                                      aria-label="Remove performance"
                                      title="Remove performance"
                                      onClick={() =>
                                        deletePerformanceRow(performance)
                                      }
                                    >
                                      <FontAwesomeIcon
                                        icon={['fas', 'trash']}
                                      />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })
                        )}
                        <tr className="stage-performances-add-row">
                          <td colSpan={5}>
                            <button
                              type="button"
                              className="btn-add-section btn-add-section--table-row"
                              onClick={() => openPerformanceModal(stage, {})}
                            >
                              <FontAwesomeIcon icon={['fas', 'plus']} />
                              Add Performance
                            </button>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <StageSponsorsGrid stageId={stage.stageId} />
              </article>
            );
          })}
        </div>
      </EventRelatedSection>

      <Portal
        header={selectedStage?.stageName || 'Add New Stage'}
        open={infoModalOpen}
        onClose={() => {
          setInfoModalOpen(false);
          setSelectedStage({});
        }}
      >
        <StageForm
          stage={selectedStage}
          onStageSave={handleStageSave}
          onDateChange={handleDateChange}
          onLocationChange={handleLocationChange}
          onLocationNameChange={handleLocationNameChange}
          onFileUpload={onFileUpload}
          onFileDelete={onFileDelete}
        />
      </Portal>

      <Portal
        header={
          selectedPerformance?.performanceId
            ? 'Edit Performance'
            : 'Add Performance'
        }
        open={perfModalOpen}
        onClose={() => {
          setPerfModalOpen(false);
          setSelectedPerformance({});
          setPerfStage(null);
        }}
      >
        <PerformanceForm
          performance={selectedPerformance}
          handleSave={savePerformance}
          handleDateChange={handlePerformanceDateChange}
          performers={performers}
          performerTypes={performerTypes}
          performerUpdated={performerUpdated}
        />
      </Portal>

      <Portal
        header="Delete stage"
        open={Boolean(stagePendingDelete)}
        onClose={cancelDeleteStage}
        size="sm"
      >
        <div className="py-2">
          <p className="m-0 text-base leading-relaxed text-[#121212]">
            Are you sure you want to delete this stage?
          </p>
          <p className="mt-2 mb-0 text-sm text-[var(--muted-foreground)]">
            Performances on this stage will also be removed.
          </p>
          <div className="mt-6 flex flex-wrap justify-end gap-3">
            <button
              type="button"
              className="inline-flex min-h-11 min-w-[120px] items-center justify-center rounded-[var(--bethere-radius-md)] border-2 border-primary bg-transparent px-5 font-bold text-primary transition-all hover:-translate-y-px hover:bg-primary hover:text-primary-foreground"
              onClick={cancelDeleteStage}
            >
              Cancel
            </button>
            <button
              type="button"
              className="inline-flex min-h-11 min-w-[120px] items-center justify-center gap-2 rounded-[var(--bethere-radius-md)] border border-transparent bg-[#c9302c] px-5 font-bold text-white transition-all hover:-translate-y-px hover:brightness-110 disabled:opacity-60"
              onClick={confirmDeleteStage}
              disabled={removeStageMutation.isPending}
            >
              <FontAwesomeIcon icon={['fas', 'trash']} />
              Delete
            </button>
          </div>
        </div>
      </Portal>
    </>
  );
}
